import React, { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ClipboardCheck, ScanSearch, CheckCheck, Check, X as XIcon, Info, FileText } from 'lucide-react';
import api, { errorMessage, toPage } from '../services/api';
import ConfirmationPopup from '../components/ConfirmationPopup';
import {
  PageHeader,
  Card,
  Segmented,
  Pagination,
  Sheet,
  Button,
  Badge,
  CopyButton,
  PlatformBadge,
  Field,
  ErrorBanner,
  SkeletonList,
  EmptyState,
  ResponsiveList,
  inputClass
} from '../components/ui';
import { formatCurrency, formatDate, fromUnix, sumItemCommission, formatNumber } from '../utils/format';
import { ACTION_ORDER, CHANGING_ACTIONS, actionMeta } from '../utils/shopeeValidation';
import { ValidationEntryCard } from './ShopeeValidationsPage';

const daysSinceUnix = (ts) => (ts ? Math.floor((Date.now() / 1000 - ts) / 86400) : 0);

// Mã kỳ thanh toán là Int64 của Shopee: giữ dạng chuỗi, không ép Number (mất chính xác)
const isValidationId = (v) => /^\d{1,19}$/.test(v) && !/^0+$/.test(v);
const formatPeriodDate = (date) => date ? date.split('-').reverse().join('/') : '';

const PREVIEW_PAGE = 60;

const ApprovalPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState('pending');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);

  // Xem trước đối soát kỳ thanh toán Shopee (khi có thì hiển thị thay cho danh sách thường)
  const [preview, setPreview] = useState(null); // { validationIds, days, entries, summary }
  const [previewFilter, setPreviewFilter] = useState('');
  const [previewShown, setPreviewShown] = useState(PREVIEW_PAGE);
  const [showForm, setShowForm] = useState(false);
  const [validationId, setValidationId] = useState('');
  const [days, setDays] = useState(14);
  const [selections, setSelections] = useState({});
  const [scanning, setScanning] = useState(false);

  const { data: periodsData, isLoading: periodsLoading, error: periodsError, refetch: refetchPeriods } = useQuery(
    ['shopee-validation-periods', days],
    async () => (await api.get('/admin/shopee-validation-periods', { params: { days } })).data.data,
    { enabled: showForm }
  );
  const periods = periodsData?.periods;
  const unappliedIds = useMemo(() => (periods || []).filter((period) => !period.runs.length).map((period) => period.validation_id), [periods]);
  const selectedIds = selections[days] || unappliedIds;
  useEffect(() => {
    if (showForm && periods) {
      setSelections((current) => current[days] ? current : { ...current, [days]: unappliedIds });
    }
  }, [showForm, periods, days, unappliedIds]);
  const selectIds = (ids) => setSelections((current) => ({ ...current, [days]: ids }));
  const manualInvalid = !!validationId && !isValidationId(validationId);
  const scanIds = [...new Set([...selectedIds, ...(validationId && !manualInvalid ? [validationId] : [])])].sort();
  const scanDisabled = scanIds.length === 0 || scanIds.length > 31 || manualInvalid;

  const [confirm, setConfirm] = useState(null); // { type: 'approve'|'reject'|'apply', order? }
  const [busy, setBusy] = useState(false);

  const approvalStatus = tab === 'pending' ? 'ready' : 'approved';
  const { data, isLoading, isFetching, error, refetch } = useQuery(
    ['approval', tab, page, limit],
    async () => toPage(await api.get('/admin/orders', { params: { approval_status: approvalStatus, page, limit } })),
    { keepPreviousData: true, enabled: !preview }
  );

  // Đếm cho tab còn lại
  const otherTab = tab === 'pending' ? 'approved' : 'pending';
  const { data: otherData } = useQuery(
    ['approval-count', otherTab],
    async () => toPage(await api.get('/admin/orders', { params: { approval_status: otherTab === 'pending' ? 'ready' : 'approved', page: 1, limit: 1 } })),
    { staleTime: 30000 }
  );

  const counts = {
    [tab]: data?.pagination?.total,
    [otherTab]: otherData?.pagination?.total
  };

  const previewList = useMemo(() => {
    if (!preview) return [];
    return previewFilter ? preview.entries.filter((e) => e.action === previewFilter) : preview.entries;
  }, [preview, previewFilter]);

  const changingCount = preview ? CHANGING_ACTIONS.reduce((n, a) => n + (preview.summary[a] || 0), 0) : 0;

  const refreshAll = () => {
    queryClient.invalidateQueries('approval');
    queryClient.invalidateQueries('approval-count');
    queryClient.invalidateQueries('dashboard');
    queryClient.invalidateQueries('shopee-validations');
    queryClient.invalidateQueries('shopee-validation-periods');
  };

  const runPreview = async () => {
    if (scanDisabled) {
      toast.error(manualInvalid ? 'Mã kỳ thanh toán phải gồm 1–19 chữ số' : 'Chọn từ 1 đến 31 ngày để đối soát');
      return;
    }
    setScanning(true);
    try {
      const { data: res } = await api.post('/admin/order-approvals/scan', { validation_ids: scanIds });
      setPreview(res.data);
      setPreviewFilter('');
      setPreviewShown(PREVIEW_PAGE);
      setShowForm(false);
      toast.success(res.message || 'Đã tải báo cáo kỳ');
    } catch (e) {
      toast.error(errorMessage(e, 'Lỗi khi đối soát với Shopee'));
    } finally {
      setScanning(false);
    }
  };

  const applyValidation = async () => {
    setBusy(true);
    try {
      const { data: res } = await api.post('/admin/order-approvals/apply', { validation_ids: preview.validationIds });
      const result = res.data;
      toast.success(`Đã áp dụng ${result.runs.length} ngày`);
      if (result.failed) {
        const failedDay = preview.days.find((day) => day.validationId === result.failed.validationId);
        toast.error(`Lỗi ngày ${formatPeriodDate(failedDay?.date) || result.failed.validationId}: ${result.failed.error}`);
      }
      const balanceErrors = result.runs.reduce((total, run) => total + (run.balanceErrors?.length || 0), 0);
      if (balanceErrors) toast.error(`Tính lại số dư lỗi ${balanceErrors} user, xem log`);
      setConfirm(null);
      setPreview(null);
      setSelections({});
      setValidationId('');
      refreshAll();
      navigate('/shopee-validations');
    } catch (e) {
      toast.error(errorMessage(e, 'Lỗi khi áp dụng đối soát'));
    } finally {
      setBusy(false);
    }
  };

  const approveOne = async (order) => {
    setBusy(true);
    try {
      await api.post(`/admin/orders/${order.id}/approve`);
      toast.success(`Đã duyệt ${order.order_sn}`);
      setConfirm(null);
      refreshAll();
    } catch (e) {
      toast.error(errorMessage(e, 'Lỗi khi duyệt đơn'));
    } finally {
      setBusy(false);
    }
  };

  const rejectOne = async (order) => {
    setBusy(true);
    try {
      await api.post(`/admin/orders/${order.id}/reject`);
      toast.success(`Đã từ chối ${order.order_sn}`);
      setConfirm(null);
      refreshAll();
    } catch (e) {
      toast.error(errorMessage(e, 'Lỗi khi từ chối đơn'));
    } finally {
      setBusy(false);
    }
  };

  const renderOrder = (order) => {
    const items = order.items || [];
    const cashback = sumItemCommission(items);
    const days = daysSinceUnix(order.complete_time);
    return (
      <Card key={order.id} className="p-4">
        <div className="flex items-start justify-between gap-2">
          <button type="button" onClick={() => navigate(`/orders/${order.id}`)} className="min-w-0 text-left">
            <p className="truncate font-mono text-sm font-semibold text-gray-900">{order.order_sn}</p>
            <p className="truncate text-xs text-gray-500">{order.user?.name || '—'} · {order.user?.email || '—'}</p>
          </button>
          <CopyButton value={order.order_sn} />
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-1.5">
          <PlatformBadge platform={order.platform} />
          <Badge tone={days >= 9 ? 'green' : 'yellow'}>{days} ngày từ khi hoàn thành</Badge>
          {order.is_withdrawable && <Badge tone="indigo">Đã duyệt</Badge>}
        </div>

        {items.length > 0 && (
          <ul className="mt-2 space-y-0.5 text-xs text-gray-600">
            {items.slice(0, 2).map((it) => <li key={it.id} className="truncate">• {it.item_name || '—'}</li>)}
            {items.length > 2 && <li className="text-gray-400">+{items.length - 2} sản phẩm khác</li>}
          </ul>
        )}

        <div className="mt-3 flex items-end justify-between gap-3 border-t border-gray-100 pt-3">
          <div>
            <p className="text-xs text-gray-500">Hoàn cho user</p>
            <p className="text-base font-semibold text-gray-900">{formatCurrency(cashback)}</p>
            <p className="text-xs text-gray-400">Hoàn thành {formatDate(fromUnix(order.complete_time))}</p>
          </div>
          {tab === 'pending' && !order.is_withdrawable && (
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="text-red-600" onClick={() => setConfirm({ type: 'reject', order })} disabled={busy}>
                <XIcon className="h-4 w-4" />
              </Button>
              <Button variant="success" size="sm" onClick={() => setConfirm({ type: 'approve', order })} disabled={busy}>
                <Check className="h-4 w-4" /> Duyệt
              </Button>
            </div>
          )}
        </div>
      </Card>
    );
  };

  const columns = [
    {
      key: 'sn',
      title: 'Mã đơn',
      render: (order) => (
        <span className="inline-flex items-center gap-1">
          <button type="button" onClick={() => navigate(`/orders/${order.id}`)} className="font-mono font-medium text-gray-900 hover:underline">{order.order_sn}</button>
          <CopyButton value={order.order_sn} />
        </span>
      )
    },
    {
      key: 'user',
      title: 'Người dùng',
      render: (order) => (
        <div>
          <p className="font-medium text-gray-900">{order.user?.name || '—'}</p>
          <p className="text-xs text-gray-500">{order.user?.email || '—'}</p>
        </div>
      )
    },
    { key: 'platform', title: 'Sàn', render: (order) => <PlatformBadge platform={order.platform} /> },
    {
      key: 'items',
      title: 'Sản phẩm',
      tdClass: 'max-w-xs',
      render: (order) => {
        const items = order.items || [];
        return (
          <div className="text-xs text-gray-600">
            <p className="truncate">{items[0]?.item_name || '—'}</p>
            {items.length > 1 && <p className="text-gray-400">+{items.length - 1} sản phẩm khác</p>}
          </div>
        );
      }
    },
    {
      key: 'done',
      title: 'Hoàn thành',
      render: (order) => {
        const days = daysSinceUnix(order.complete_time);
        return (
          <div className="whitespace-nowrap">
            <p>{formatDate(fromUnix(order.complete_time))}</p>
            <p className={days >= 9 ? 'text-xs text-emerald-700' : 'text-xs text-amber-700'}>{days} ngày trước</p>
          </div>
        );
      }
    },
    { key: 'cb', title: 'Hoàn cho user', align: 'right', render: (order) => <span className="whitespace-nowrap font-semibold text-gray-900">{formatCurrency(sumItemCommission(order.items))}</span> },
    {
      key: 'actions',
      title: '',
      align: 'right',
      render: (order) => (order.is_withdrawable ? (
        <Badge tone="indigo">Đã duyệt</Badge>
      ) : tab === 'pending' ? (
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" className="text-red-600" onClick={() => setConfirm({ type: 'reject', order })} disabled={busy} aria-label={`Từ chối ${order.order_sn}`}>
            <XIcon className="h-4 w-4" />
          </Button>
          <Button variant="success" size="sm" onClick={() => setConfirm({ type: 'approve', order })} disabled={busy}>
            <Check className="h-4 w-4" /> Duyệt
          </Button>
        </div>
      ) : null)
    }
  ];

  const orders = data?.items || [];
  const s = preview?.summary || {};

  return (
    <div>
      <PageHeader
        title="Đối soát & duyệt đơn"
        subtitle="Shopee duyệt theo kỳ thanh toán Shopee đã xác thực; Lazada tự duyệt theo ngày, TikTok khi quyết toán"
        actions={
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => navigate('/shopee-validations')}>
              <FileText className="h-4 w-4" /> Báo cáo
            </Button>
            <Button variant="soft" onClick={() => setShowForm(true)}>
              <ScanSearch className="h-4 w-4" /> Đối soát kỳ Shopee
            </Button>
          </div>
        }
      />

      {preview ? (
        <Card className="mb-4 border-amber-200 bg-amber-50/50 p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="text-sm">
              <p className="font-semibold text-amber-900">Xem trước đối soát {preview.validationIds.length} ngày</p>
              <div className="my-2 flex flex-wrap gap-1.5">
                {preview.days.map((day) => (
                  <Badge key={day.validationId} tone="gray">
                    {formatPeriodDate(day.date) || day.validationId}: {formatNumber(day.reportOrders)} đơn
                  </Badge>
                ))}
              </div>
              <p className="text-amber-900">
                {formatNumber(s.reportOrders || 0)} đơn trong báo cáo ({preview.days.reduce((total, day) => total + day.pages, 0)} trang) · {formatNumber(changingCount)} đơn sẽ thay đổi
                · hoàn cho user {formatCurrency(s.totalBefore)} → {formatCurrency(s.totalAfter)}
              </p>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setPreview(null)} disabled={busy}>Đóng</Button>
          </div>
          <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <Segmented
              value={previewFilter}
              onChange={(v) => { setPreviewFilter(v); setPreviewShown(PREVIEW_PAGE); }}
              options={[
                { value: '', label: 'Tất cả', count: s.reportOrders },
                ...ACTION_ORDER.filter((a) => s[a] > 0).map((a) => ({ value: a, label: actionMeta(a).label, count: s[a] }))
              ]}
            />
            <Button variant="success" onClick={() => setConfirm({ type: 'apply' })} disabled={busy || changingCount === 0}>
              <CheckCheck className="h-4 w-4" /> Áp dụng ({formatNumber(changingCount)} đơn)
            </Button>
          </div>
        </Card>
      ) : (
        <Segmented
          className="mb-4"
          value={tab}
          onChange={(v) => { setTab(v); setPage(1); }}
          options={[
            { value: 'pending', label: 'Chờ duyệt', count: counts.pending },
            { value: 'approved', label: 'Đã duyệt', count: counts.approved }
          ]}
        />
      )}

      {preview ? (
        previewList.length === 0 ? (
          <EmptyState icon={ClipboardCheck} title="Không có đơn trong nhóm này" />
        ) : (
          <>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
              {previewList.slice(0, previewShown).map((e) => (
                <ValidationEntryCard key={e.order_sn} entry={e} onOpenOrder={(id) => navigate(`/orders/${id}`)} />
              ))}
            </div>
            {previewList.length > previewShown && (
              <div className="mt-4 text-center">
                <Button variant="outline" onClick={() => setPreviewShown((n) => n + PREVIEW_PAGE)}>
                  Xem thêm ({formatNumber(previewList.length - previewShown)})
                </Button>
              </div>
            )}
          </>
        )
      ) : (
        <>
          <ErrorBanner message={error ? (errorMessage(error, 'Lỗi khi tải danh sách đơn')) : ''} onRetry={refetch} />
          {isLoading ? (
            <SkeletonList />
          ) : (
            <ResponsiveList
              items={orders}
              renderCard={renderOrder}
              columns={columns}
              empty={<EmptyState icon={ClipboardCheck} title={tab === 'pending' ? 'Không có đơn chờ duyệt' : 'Chưa có đơn đã duyệt'} />}
            />
          )}
          {data?.pagination?.total > 0 && (
            <Pagination
              page={page}
              totalPages={data.pagination.pages}
              total={data.pagination.total}
              onChange={setPage}
              limit={limit}
              onLimitChange={(n) => { setLimit(n); setPage(1); }}
              disabled={isFetching}
            />
          )}
        </>
      )}

      {/* Form đối soát kỳ thanh toán */}
      <Sheet
        open={showForm}
        onClose={() => !scanning && setShowForm(false)}
        title="Đối soát kỳ thanh toán Shopee"
        footer={<Button className="w-full" onClick={runPreview} loading={scanning} disabled={scanDisabled}>Xem trước {scanIds.length} ngày</Button>}
      >
        <div className="space-y-3">
          <fieldset disabled={scanning} className="space-y-3">
          <Field label="Số ngày báo cáo">
            <select className={inputClass} value={days} onChange={(e) => setDays(Number(e.target.value))}>
              {[14, 31, 62].map((value) => <option key={value} value={value}>{value} ngày</option>)}
            </select>
          </Field>
          <ErrorBanner message={periodsError ? errorMessage(periodsError, 'Lỗi khi tải kỳ thanh toán Shopee') : ''} onRetry={refetchPeriods} />
          <div className="flex flex-wrap gap-2">
            <Button variant="soft" size="sm" onClick={() => selectIds(unappliedIds)} disabled={!periods}>Chọn ngày chưa áp dụng</Button>
            <Button variant="ghost" size="sm" onClick={() => { selectIds([]); setValidationId(''); }}>Bỏ chọn</Button>
          </div>
          {periodsLoading ? <SkeletonList rows={3} /> : (
            <div className="max-h-80 space-y-2 overflow-y-auto">
              {(periods || []).map((period) => (
                <label key={period.validation_id} className="flex cursor-pointer items-center gap-3 rounded-xl border border-gray-200 p-3">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-gray-300 accent-gray-900"
                    checked={selectedIds.includes(period.validation_id)}
                    onChange={(e) => selectIds(e.target.checked ? [...selectedIds, period.validation_id] : selectedIds.filter((id) => id !== period.validation_id))}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium text-gray-900">{formatPeriodDate(period.date)}</span>
                    <span className="block font-mono text-xs text-gray-500">{period.validation_id}</span>
                  </span>
                  <Badge tone={period.runs.length ? 'indigo' : 'yellow'}>
                    {period.runs.length ? `Đã áp dụng (${period.runs.length})` : 'Chưa áp dụng'}
                  </Badge>
                </label>
              ))}
              {periods?.length === 0 && <p className="text-sm text-gray-500">Không có kỳ thanh toán</p>}
            </div>
          )}
          <p className="text-xs text-gray-500">Báo cáo ngày D gồm đơn hoàn thành ngày D-1</p>
          {scanIds.length > 31 && <p className="text-sm text-amber-700">Đã chọn {scanIds.length} ngày. Chỉ được đối soát tối đa 31 ngày mỗi lần.</p>}
          <details className="rounded-xl border border-gray-200 p-3">
            <summary className="cursor-pointer text-sm font-medium text-gray-700">Nhập mã thủ công</summary>
          <Field className="mt-3" label="Mã kỳ thanh toán (Validation ID)" hint="Mã gồm 1–19 chữ số, được thêm vào các ngày đã chọn; mã trùng chỉ tính một lần.">
            <input
              type="text"
              inputMode="numeric"
              autoComplete="off"
              className={inputClass}
              placeholder="VD: 17321670058261028"
              value={validationId}
              onChange={(e) => setValidationId(e.target.value.replace(/\D/g, ''))}
            />
          </Field>
          {manualInvalid && <p className="mt-1 text-xs text-red-600">Mã kỳ thanh toán phải gồm 1–19 chữ số</p>}
          </details>
          </fieldset>
          <div className="flex gap-2 rounded-xl bg-blue-50 p-3 text-xs text-blue-800">
            <Info className="h-4 w-4 flex-shrink-0" />
            <span>
              Hệ thống tải toàn bộ báo cáo kỳ từ Shopee Open API. Đơn chỉ được duyệt khi mọi sản phẩm còn hiệu lực là
              COMPLETED (kể cả hoàn một phần) + VERIFIED; hoa hồng được ghi theo số Shopee xác thực, sản phẩm FRAUD bị huỷ. Xem trước không ghi gì;
              bấm Áp dụng mới thay đổi và lưu vào Báo cáo.
            </span>
          </div>
        </div>
      </Sheet>

      <ConfirmationPopup
        isOpen={!!confirm}
        onClose={() => !busy && setConfirm(null)}
        onConfirm={() => {
          if (confirm?.type === 'approve') approveOne(confirm.order);
          else if (confirm?.type === 'reject') rejectOne(confirm.order);
          else if (confirm?.type === 'apply') applyValidation();
        }}
        title={confirm?.type === 'reject' ? 'Từ chối đơn hàng' : confirm?.type === 'apply' ? 'Áp dụng đối soát' : 'Duyệt đơn hàng'}
        message={
          confirm?.type === 'reject'
            ? `Đơn ${confirm?.order?.order_sn} sẽ chuyển sang "Đã hủy" và hoa hồng bị loại khỏi số dư của người dùng.`
            : confirm?.type === 'apply'
              ? `Áp dụng đối soát ${preview?.validationIds.length} ngày: duyệt ${formatNumber((s.approved || 0) + (s.approved_adjusted || 0) + (s.approved_with_fraud || 0))} đơn, huỷ ${formatNumber((s.cancelled || 0) + (s.cancelled_fraud || 0))} đơn. Hệ thống tải lại báo cáo từ Shopee trước khi ghi.`
              : `Duyệt tay đơn ${confirm?.order?.order_sn}? Hoàn tiền ${formatCurrency(sumItemCommission(confirm?.order?.items))} sẽ chuyển sang số dư khả dụng.`
        }
        confirmText={confirm?.type === 'reject' ? 'Từ chối' : confirm?.type === 'apply' ? 'Áp dụng' : 'Duyệt'}
        type={confirm?.type === 'reject' ? 'danger' : 'success'}
        isLoading={busy}
      />
    </div>
  );
};

export default ApprovalPage;
